import { Component, inject, Input, OnChanges } from '@angular/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { DateTime } from 'luxon';
import { TranslateService } from '@ngx-translate/core';

type TimelineEvent = {
    label: string;
    date: Date | null;
    dateString: string;
    position: number;
    color: string;
    isNow?: boolean;
    labelAbove?: boolean;
};

@Component({
    selector: 'app-agreement-timeline',
    templateUrl: './agreement-timeline.component.html',
    styleUrls: ['./agreement-timeline.component.scss']
})
export class AgreementTimelineComponent implements OnChanges {
    @Input() public validFrom: NgbDateStruct;
    @Input() public noticeDate: Date | null;
    @Input() public validTo: NgbDateStruct;
    @Input() public terminationDate: NgbDateStruct;
    @Input() public validToAfterExtension: Date | null;

    private readonly translateService = inject(TranslateService);

    public events: TimelineEvent[] = [];
    public segments: { start: number; end: number; color: string; isDotted?: boolean }[] = [];

    public ngOnChanges(): void {
        this.updateTimeline();
    }

    private updateTimeline(): void {
        const events: TimelineEvent[] = [];
        const dates: Date[] = [];
        const now = DateTime.now();
        const nowDate = now.toJSDate();
        let showNowMarker = !!this.validFrom; // Only show Now if validFrom is set

        // Add validFrom date
        if (this.validFrom) {
            const validFromDateTime = DateTime.fromObject({
                year: this.validFrom.year,
                month: this.validFrom.month,
                day: this.validFrom.day
            });
            if (validFromDateTime.isValid) {
                const validFromDate = validFromDateTime.toJSDate();
                events.push({
                    label: this.translateService.instant('FEATURES.AGREEMENTS.VALIDFROM') as string,
                    date: validFromDate,
                    dateString: this.formatDate(validFromDateTime),
                    position: 0,
                    color: '#10b981'
                });
                dates.push(validFromDate);
            }
        }

        // Add notice date
        if (this.noticeDate) {
            events.push({
                label: this.translateService.instant('FEATURES.AGREEMENTS.NOTICE_PERIOD') as string,
                date: this.noticeDate,
                dateString: this.formatDate(DateTime.fromJSDate(this.noticeDate)),
                position: 0,
                color: '#f59e0b'
            });
            dates.push(this.noticeDate);
        }

        // Add validTo date
        if (this.validTo) {
            const validToDateTime = DateTime.fromObject({
                year: this.validTo.year,
                month: this.validTo.month,
                day: this.validTo.day
            });
            if (validToDateTime.isValid) {
                const validToDate = validToDateTime.toJSDate();
                events.push({
                    label: this.translateService.instant('FEATURES.AGREEMENTS.VALIDTO') as string,
                    date: validToDate,
                    dateString: this.formatDate(validToDateTime),
                    position: 0,
                    color: '#ef4444'
                });
                dates.push(validToDate);
            }
        }

        // Add validToAfterExtension date
        if (this.validToAfterExtension) {
            events.push({
                label: this.translateService.instant('FEATURES.AGREEMENTS.DATE_AFTER_EXTENSION') as string,
                date: this.validToAfterExtension,
                dateString: this.formatDate(DateTime.fromJSDate(this.validToAfterExtension)),
                position: 0,
                color: '#ef4444'
            });
            dates.push(this.validToAfterExtension);
        }

        // Add termination date
        if (this.terminationDate) {
            const terminationDateTime = DateTime.fromObject({
                year: this.terminationDate.year,
                month: this.terminationDate.month,
                day: this.terminationDate.day
            });
            if (terminationDateTime.isValid) {
                const terminationDateObj = terminationDateTime.toJSDate();
                events.push({
                    label: this.translateService.instant('FEATURES.AGREEMENTS.TERMINATION_DATE') as string,
                    date: terminationDateObj,
                    dateString: this.formatDate(terminationDateTime),
                    position: 0,
                    color: '#dc2626'
                });
                dates.push(terminationDateObj);

                // Don't show "Now" marker if current date is after termination date
                if (now > terminationDateTime) {
                    showNowMarker = false;
                }
            }
        }

        // Add "Now" marker (only if not after termination date)
        if (showNowMarker) {
            events.push({
                label: this.translateService.instant('GENERAL.NOW') as string,
                date: nowDate,
                dateString: this.formatDate(now),
                position: 0,
                color: '#8b5cf6',
                isNow: true
            });
            dates.push(nowDate);
        }

        // Calculate positions
        if (dates.length > 0) {
            const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
            const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));
            const range = maxDate.getTime() - minDate.getTime();

            events.forEach((event) => {
                if (event.date) {
                    if (range === 0) {
                        event.position = 50;
                    } else {
                        event.position = ((event.date.getTime() - minDate.getTime()) / range) * 100;
                    }
                }
            });

            // Sort events by position
            events.sort((a, b) => a.position - b.position);

            // Assign label positions to avoid overlaps
            this.assignLabelPositions(events);

            // Create segments between events
            this.segments = [];
            const validToLabel = this.translateService.instant('FEATURES.AGREEMENTS.VALIDTO') as string;
            const afterExtensionLabel = this.translateService.instant(
                'FEATURES.AGREEMENTS.DATE_AFTER_EXTENSION'
            ) as string;
            for (let i = 0; i < events.length - 1; i++) {
                const isDotted = events[i].label === validToLabel && events[i + 1].label === afterExtensionLabel;
                this.segments.push({
                    start: events[i].position,
                    end: events[i + 1].position,
                    color: this.getSegmentColor(i),
                    isDotted: isDotted
                });
            }
        }

        this.events = events;
    }

    private assignLabelPositions(events: TimelineEvent[]): void {
        const minSpacing = 15; // Minimum spacing percentage to avoid overlap

        for (let i = 0; i < events.length; i++) {
            if (i === 0) {
                // First event defaults to above
                events[i].labelAbove = true;
            } else {
                const spacing = events[i].position - events[i - 1].position;
                if (spacing < minSpacing) {
                    // Too close: alternate positions
                    events[i].labelAbove = !events[i - 1].labelAbove;
                } else {
                    // Enough space: still alternate to distribute labels evenly
                    events[i].labelAbove = !events[i - 1].labelAbove;
                }
            }
        }
    }

    private getSegmentColor(index: number): string {
        const colors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444'];
        return colors[index % colors.length];
    }

    private formatDate(dateTime: DateTime): string {
        if (!dateTime?.isValid) return '';
        return dateTime.toFormat('yyyy-MM-dd');
    }
}
